import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useState } from 'react';

import {
  formatDate,
  formatMoney,
} from '../../../../../../../../lib/functions/utils';
import CategoryModal from '../../../DailyBook/components/Modal';

const useStyles = makeStyles(() =>
  createStyles({
    tableCell: {
      padding: '8px !important',
      overflow: 'hidden',
      minWidth: '50px',
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important',
    },
    clickableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
  }),
);

interface MovementsTableProps {
  groupedLines: any[];
  groupedByCategoryAndBusiness: Record<string, Record<string, any[]>>;
}

const MovementsTable = ({
  groupedLines,
  groupedByCategoryAndBusiness,
}: MovementsTableProps) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  const handleRowClick = (row: any) => {
    const foundGroup = findGroupByMovementKey(row);
    setSelectedCategory(foundGroup);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  const findGroupByMovementKey = (
    row: any,
  ): {
    date: string;
    category: string;
    groupedLines: any[];
    businessName: string;
    businessIdentifier: string;
  } | null => {
    const formattedDate = formatDate(row.date, 'yyyy-mm-dd');
    const dateGroups = groupedByCategoryAndBusiness?.[formattedDate];
    if (!dateGroups) {
      return null;
    }
    const allEntries = Object.values(dateGroups).flat();
    const matchingEntry = allEntries.find(
      (entry: any) => entry.movementKey === row.movementKey,
    );
    if (matchingEntry) {
      const categoryAndAccount = Object.keys(dateGroups).find((key) =>
        dateGroups[key].some(
          (entry: any) => entry.movementKey === row.movementKey,
        ),
      );
      return {
        date: formattedDate,
        category: matchingEntry.category,
        groupedLines: dateGroups[categoryAndAccount!],
        businessName: matchingEntry.businessName,
        businessIdentifier: matchingEntry.businessIdentifier,
      };
    }
    return null;
  };

  return (
    <>
      <Paper elevation={0} style={{ padding: 16, margin: '12px 0' }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          style={{ marginBottom: 8, fontWeight: 600 }}
        >
          {groupedLines[0].accountName}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Debe</TableCell>
              <TableCell>Haber</TableCell>
              <TableCell>Débito</TableCell>
              <TableCell>Crédito</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedLines.map((category: any, catIndex: number) => (
              <React.Fragment key={catIndex}>
                <TableRow
                  className={classes.clickableRow}
                  onClick={() => handleRowClick(category)}
                >
                  <TableCell className={classes.tableCell}>
                    {formatDate(category.date)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {category.description}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.debit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.credit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.debitFinal)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.creditFinal)}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            <TableRow>
              <TableCell
                colSpan={2}
                style={{
                  fontWeight: 'bold',
                  textAlign: 'left',
                }}
              >
                Totales:
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.debit,
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.credit,
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.debitFinal,
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.creditFinal,
                    0,
                  ),
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {selectedCategory && (
          <CategoryModal
            open={modalOpen}
            onClose={handleCloseModal}
            category={selectedCategory}
          />
        )}
      </Paper>
    </>
  );
};

export default MovementsTable;
